import { Component, Inject } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import {
    MAT_MOMENT_DATE_FORMATS,
    MomentDateAdapter,
} from '@angular/material-moment-adapter';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { CreateOrUpdateLicencePackageDto } from '../../dtos/CreateLicencePackageDto.interface';
import { PackageType } from '../../enums/package-type.enum';

import 'moment/locale/de-ch';

@Component({
    selector: 'app-create-licence-package',
    templateUrl: './create-licence-package.component.html',
    styleUrls: ['./create-licence-package.component.scss'],
    providers: [
        { provide: DateAdapter, useClass: MomentDateAdapter, deps: [MAT_DATE_LOCALE] },
        { provide: MAT_DATE_LOCALE, useValue: 'de-ch' },
        { provide: MAT_DATE_FORMATS, useValue: MAT_MOMENT_DATE_FORMATS }
    ]
})
export class CreateOrUpdateLicencePackageComponent {
    // Available package types derived from the PackageType enum
    packageTypes: PackageType[] = Object.values(PackageType);

    // Reactive form group for licence package creation
    licenceForm: FormGroup;

    constructor(
        public dialogRef: MatDialogRef<CreateOrUpdateLicencePackageComponent>,
        @Inject(MAT_DIALOG_DATA) public data: { organizationId: string; package: CreateOrUpdateLicencePackageDto },
        private fb: FormBuilder,
    ) {
        // Initialize the form with form controls and validators
        this.licenceForm = this.fb.group(
            {
                type: [null, Validators.required],
                packageSize: [null, [Validators.required, Validators.min(1)]],
                startDate: [null, Validators.required],
                endDate: [null, Validators.required],
                description: [''],
                price: [null, [Validators.min(0)]],
                discountAmount: [null, [Validators.min(0)]],
                discountPercent: [null, [Validators.min(0), Validators.max(100)]],
            },
            {
                validators: this.endDateAfterStartDate,
            },
        );

        if (this.data.package) {
            this.licenceForm.patchValue({
                type: this.data.package.type,
                packageSize: this.data.package.packageSize,
                startDate: new Date(this.data.package.startDate),
                endDate: new Date(this.data.package.endDate),
                description: this.data.package.description,
                price: this.data.package.price,
                discountAmount: this.data.package.discountAmount,
                discountPercent: this.data.package.discountPercent,
            });
        }
    }

    /**
     * Custom validator to ensure that endDate is after startDate
     * @param formGroup - The form group containing startDate and endDate
     * @returns ValidationErrors or null
     */
    endDateAfterStartDate(formGroup: FormGroup): ValidationErrors | null {
        const start = formGroup.get('startDate')?.value;
        const end = formGroup.get('endDate')?.value;
        if (start && end && new Date(end) <= new Date(start)) {
            return { endDateBeforeStartDate: true };
        }
        return null;
    }

    /**
     * Closes the dialog without saving
     */
    onCancel(): void {
        this.dialogRef.close();
    }

    /**
     * Saves the licence package by sending the form data to the backend
     */
    onSave(): void {
        if (this.licenceForm.valid) {
            const formValue = this.licenceForm.value;
            
            // Convert Moment objects to JavaScript Date objects
            const startDate = formValue.startDate?.toDate ? formValue.startDate.toDate() : formValue.startDate;
            const endDate = formValue.endDate?.toDate ? formValue.endDate.toDate() : formValue.endDate;
            
            const createLicencePackageDto: CreateOrUpdateLicencePackageDto = {
                type: formValue.type,
                packageSize: formValue.packageSize,
                startDate: new Date(Date.UTC(startDate.getFullYear(), startDate.getMonth(), startDate.getDate(), 0, 0, 0, 0)).toISOString(),
                endDate: new Date(Date.UTC(endDate.getFullYear(), endDate.getMonth(), endDate.getDate(), 0, 0, 0, 0)).toISOString(),
                organizationId: this.data.organizationId,
                description: formValue.description,
                price: formValue.price,
                discountAmount: formValue.discountAmount,
                discountPercent: formValue.discountPercent,
            };

            this.dialogRef.close(createLicencePackageDto);
        }
    }

    /**
     * Helper method to access form controls in the template
     */
    get f(): { [key: string]: AbstractControl } {
        return this.licenceForm.controls;
    }
}
